import axios from 'axios';

// Constants for the OpenAI API
const OPENAI_API = {
  BASE_URL: 'https://api.openai.com/v1/chat/completions',
  HEADERS: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer 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'
  },
  MODEL: 'gpt-4o-mini',
  DEFAULT_TEMPERATURE: 0.7
};

/**
 * Service to generate AI-powered recommendations, action plans, and surveys based on prompts
 */
class AiService {
  /**
   * Generate AI recommendations based on survey results
   * @param {Object} surveyData - Data from a completed survey
   * @param {Object} tenantInfo - Information about the current tenant
   * @returns {Promise<Array>} - Array of recommendations
   */
  static async generateRecommendations(surveyData, tenantInfo) {
    try {
      // Create a prompt that includes survey data and tenant context
      const promptText = this._buildRecommendationPrompt(surveyData, tenantInfo);
      
      // Call the OpenAI API
      const response = await this._callOpenAIApi(promptText);
      
      // Parse and structure the response
      return this._parseRecommendations(response, surveyData);
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      // Return fallback recommendations
      return this._getFallbackRecommendations(surveyData);
    }
  }
  
  /**
   * Generate AI action plans based on survey results and selected recommendations
   * @param {Object} surveyData - Data from a completed survey
   * @param {Array} selectedRecommendations - Recommendations selected by the user
   * @param {Object} tenantInfo - Information about the current tenant
   * @returns {Promise<Object>} - Action plan object
   */
  static async generateActionPlan(surveyData, selectedRecommendations, tenantInfo) {
    try {
      // Create a prompt that includes selected recommendations and tenant context
      const promptText = this._buildActionPlanPrompt(surveyData, selectedRecommendations, tenantInfo);
      
      // Call the OpenAI API
      const response = await this._callOpenAIApi(promptText);
      
      // Parse and structure the response
      return this._parseActionPlan(response, selectedRecommendations);
    } catch (error) {
      console.error('Error generating AI action plan:', error);
      // Return a fallback action plan
      return this._getFallbackActionPlan(selectedRecommendations);
    }
  }

  /**
   * Generate AI survey based on topic and number of questions
   * @param {Object} surveyOptions - Options for survey generation (topic, questionCount, language)
   * @returns {Promise<Object>} - Generated survey object
   */
  static async generateSurvey(surveyOptions) {
    try {
      // Create a prompt for survey generation
      const promptText = this._buildSurveyPrompt(surveyOptions);
      
      // Call the OpenAI API
      const response = await this._callOpenAIApi(promptText);
      
      // Parse and structure the response into a survey
      return this._parseSurveyResponse(response, surveyOptions);
    } catch (error) {
      console.error('Error generating AI survey:', error);
      return this._getFallbackSurvey(surveyOptions);
    }
  }
  
  /**
   * Call the OpenAI API with a given prompt
   * @param {string} promptText - The prompt to send to the API
   * @returns {Promise<Object>} - The API response
   * @private
   */
  static async _callOpenAIApi(promptText) {
    const data = {
      model: OPENAI_API.MODEL,
      messages: [
        {
          role: "system",
          content: "You are a helpful assistant specialized in creating surveys based on user requirements. Return all responses in JSON format."
        },
        {
          role: "user",
          content: promptText
        }
      ],
      temperature: OPENAI_API.DEFAULT_TEMPERATURE
    };
    
    try {
      const response = await axios.post(
        OPENAI_API.BASE_URL,
        data,
        { headers: OPENAI_API.HEADERS }
      );
      
      return {
        response: response.data.choices[0]?.message?.content || ''
      };
    } catch (error) {
      console.error('OpenAI API Error:', error.response?.data || error.message);
      throw new Error('Failed to generate content with AI');
    }
  }
  
  /**
   * Build a prompt for generating a survey
   * @param {Object} options - Survey generation options
   * @param {string} options.topic - The topic for the survey
   * @param {number} options.questionCount - Number of questions (default: 5)
   * @param {string} options.language - Language to generate the survey in (default: Turkish)
   * @param {Array} options.questionTypes - Types of questions to include (default: all types)
   * @returns {string} - Formatted prompt for survey generation
   * @private
   */
  static _buildSurveyPrompt(options) {
    const { 
      topic, 
      questionCount = 5, 
      language = 'Turkish',
      questionTypes = ['multiple_choice', 'single_choice', 'yes_no'] 
    } = options;
    
    // Create question type descriptions for the prompt
    const questionTypeDescriptions = {
      multiple_choice: 'Multiple choice questions (include 4-5 options per question)',
      single_choice: 'Single choice questions (include 4-5 options per question)',
      yes_no: 'Yes/No questions',
      rating: 'Rating questions (1-5 scale)'
    };
    
    // Filter question types based on user selection
    const selectedTypes = Object.entries(questionTypeDescriptions)
      .filter(([type, _]) => questionTypes.includes(type))
      .map(([_, description]) => `- ${description}`)
      .join('\n');
    
    // Define question type mapping for the prompt
    const typeMapping = {
      multiple_choice: 'multiple_choice',
      single_choice: 'single_choice',
      yes_no: 'yes_no',
      rating: 'rating'
    };
    
    // Create a list of allowed types for the prompt
    const allowedTypes = questionTypes
      .map(type => `"${typeMapping[type]}"`)
      .join(', ');
    
    return `
    Generate a professional ${language} survey about "${topic}" with ${questionCount} questions.
    
    Include ONLY the following types of questions:
    ${selectedTypes}
    
    For each question, provide:
    1. Question text
    2. A short description/context for the question
    3. Question type (only use: ${allowedTypes})
    4. Options (for multiple/single choice questions)
    
    Also provide:
    - A suitable title for the survey
    - A brief description of the survey
    
    Format your response as structured data in valid JSON format like this:
    {
      "title": "Survey Title",
      "description": "Survey description",
      "questions": [
        {
          "text": "Question text",
          "description": "Question description",
          "type": "multiple_choice",
          "options": ["Option 1", "Option 2", "Option 3", "Option 4"]
        },
        {
          "text": "Another question",
          "description": "Another description",
          "type": "yes_no",
          "options": []
        }
      ]
    }
    
    IMPORTANT: Make sure your response is a properly formatted JSON object that can be parsed directly. Don't include any explanations or markdown formatting. Just return the JSON.
    `;
  }
  
  /**
   * Validate and normalize question type
   * @param {string} type - The question type from AI
   * @param {Array} allowedTypes - List of allowed question types
   * @returns {string} - Normalized question type
   * @private
   */
  static _validateQuestionType(type, allowedTypes = ['multiple_choice', 'single_choice', 'yes_no', 'rating']) {
    const normalizedType = type?.toLowerCase() || '';
    
    // First check if the type is directly in the allowed types
    if (allowedTypes.includes(normalizedType)) {
      return normalizedType;
    }
    
    // Infer type from string if possible
    if (allowedTypes.includes('multiple_choice') && normalizedType.includes('multiple')) return 'multiple_choice';
    if (allowedTypes.includes('single_choice') && normalizedType.includes('single')) return 'single_choice';
    if (allowedTypes.includes('yes_no') && (normalizedType.includes('yes') || normalizedType.includes('no'))) return 'yes_no';
    if (allowedTypes.includes('rating') && normalizedType.includes('rating')) return 'rating';
    
    // Default to the first allowed type as fallback
    return allowedTypes[0] || 'multiple_choice';
  }
  
  /**
   * Parse the response from the AI to extract a structured survey
   * @param {Object} response - The response from the OpenAI API
   * @param {Object} options - The original survey options 
   * @returns {Object} - Structured survey object
   * @private
   */
  static _parseSurveyResponse(response, options) {
    try {
      const responseText = response.response || '';
      
      // Extract JSON from the response
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const jsonStr = jsonMatch[0];
        let surveyData;
        
        try {
          surveyData = JSON.parse(jsonStr);
        } catch (err) {
          console.error('Error parsing JSON from AI response:', err);
          console.log('Response text:', responseText);
          throw new Error('Invalid JSON in response');
        }
        
        // Get the allowed question types
        const allowedTypes = options.questionTypes || ['multiple_choice', 'single_choice', 'yes_no', 'rating'];
        
        // Validate and ensure all required fields are present
        return {
          title: surveyData.title || `${options.topic} Survey`,
          description: surveyData.description || `A survey about ${options.topic}`,
          questions: (surveyData.questions || []).map((q, index) => ({
            text: q.text || `Question ${index + 1}`,
            description: q.description || '',
            type: this._validateQuestionType(q.type, allowedTypes),
            options: q.options || [],
            required: true
          }))
        };
      }
      
      throw new Error('Could not extract valid JSON from response');
    } catch (error) {
      console.error('Error parsing survey response:', error);
      return this._getFallbackSurvey(options);
    }
  }
  
  /**
   * Generate a fallback survey if the AI generation fails
   * @param {Object} options - The original survey options
   * @returns {Object} - A basic survey as fallback
   * @private
   */
  static _getFallbackSurvey(options) {
    const { 
      topic, 
      questionCount = 5, 
      language = 'Turkish',
      questionTypes = ['multiple_choice', 'single_choice', 'yes_no', 'rating']
    } = options;
    
    const isEnglish = language.toLowerCase() === 'english';
    
    // If no question types selected, default to multiple_choice
    const availableTypes = questionTypes.length > 0 ? questionTypes : ['multiple_choice'];
    
    const questions = [];
    
    for (let i = 0; i < Math.min(questionCount, 10); i++) {
      // Cycle through available question types
      const type = availableTypes[i % availableTypes.length];
      
      let question = {
        text: isEnglish 
          ? `What is your opinion about ${topic}?` 
          : `${topic} hakkındaki düşünceniz nedir?`,
        description: isEnglish
          ? 'Please provide your honest tickets'
          : 'Lütfen dürüst geri bildiriminizi paylaşın',
        type,
        options: [],
        required: true
      };
      
      // Add appropriate options based on question type
      if (type === 'multiple_choice' || type === 'single_choice') {
        question.options = isEnglish
          ? ['Very satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very dissatisfied']
          : ['Çok memnunum', 'Memnunum', 'Kararsızım', 'Memnun değilim', 'Hiç memnun değilim'];
      } else if (type === 'yes_no') {
        question.options = isEnglish 
          ? ['Yes', 'No'] 
          : ['Evet', 'Hayır'];
      } else if (type === 'rating') {
        // Rating doesn't need options, but we'll set a custom text
        question.text = isEnglish
          ? `Rate your satisfaction with ${topic} from 1 to 5`
          : `${topic} ile ilgili memnuniyetinizi 1-5 arasında değerlendirin`;
      }
      
      questions.push(question);
    }
    
    return {
      title: isEnglish ? `${topic} Survey` : `${topic} Anketi`,
      description: isEnglish 
        ? `This survey aims to collect tickets about ${topic}`
        : `Bu anket, ${topic} hakkında geri bildirim toplamayı amaçlamaktadır`,
      questions
    };
  }
  
  /**
   * Build a prompt for generating recommendations
   * @param {Object} surveyData - Data from a completed survey
   * @param {Object} tenantInfo - Information about the current tenant
   * @returns {string} - The formatted prompt
   * @private
   */
  static _buildRecommendationPrompt(surveyData, tenantInfo) {
    return `
    You are an experienced HR consultant analyzing survey data for ${tenantInfo?.name || 'a company'}.
    
    Survey Title: ${surveyData.title}
    Completion Rate: ${surveyData.completionRate}%
    Average Score: ${surveyData.averageScore}/5
    
    Category Scores:
    ${surveyData.categories.map(cat => `- ${cat.name}: ${cat.score}/5`).join('\n')}
    
    Based on this survey data, generate 3 specific recommendations to improve employee satisfaction.
    For each recommendation, include:
    1. A clear title
    2. A brief description of the issue
    3. At least 4 specific action items
    4. Expected impact (High, Medium, or Low)
    
    Format your response as structured data that can be parsed programmatically.
    `;
  }
  
  /**
   * Build a prompt for generating an action plan
   * @param {Object} surveyData - Data from a completed survey
   * @param {Array} selectedRecommendations - Recommendations selected by the user
   * @param {Object} tenantInfo - Information about the current tenant
   * @returns {string} - The formatted prompt
   * @private
   */
  static _buildActionPlanPrompt(surveyData, selectedRecommendations, tenantInfo) {
    const recommendationsText = selectedRecommendations
      .map(rec => `- ${rec.title}: ${rec.description}`)
      .join('\n');
      
    const actionsText = selectedRecommendations
      .flatMap(rec => rec.actionItems || [])
      .map(action => `- ${action}`)
      .join('\n');
    
    return `
    You are a strategic HR consultant creating an action plan for ${tenantInfo?.name || 'a company'}.
    
    Based on the survey "${surveyData.title}" and the following selected recommendations:
    ${recommendationsText}
    
    And these specific actions:
    ${actionsText}
    
    Create a detailed action plan with:
    1. A compelling title for the overall plan
    2. A list of concrete tasks with estimated completion timeframes
    3. Key performance indicators to measure success
    4. Potential challenges and mitigation strategies
    
    Format your response as structured data that can be parsed programmatically.
    `;
  }
  
  /**
   * Parse the API response to extract recommendations
   * @param {Object} response - The API response
   * @param {Object} surveyData - Original survey data
   * @returns {Array} - Parsed recommendations
   * @private
   */
  static _parseRecommendations(response, surveyData) {
    // In a real implementation, you would parse the API response
    // For now, we'll return a simplified mock structure
    try {
      const responseText = response.response || '';
      
      // Simple parsing logic - this would be more sophisticated in production
      const recommendationSections = responseText.split(/Recommendation \d+:/i).filter(Boolean);
      
      return recommendationSections.map((section, index) => {
        // Extract title - first line after separating
        const titleMatch = section.match(/^(.+?)(?:\n|$)/);
        const title = titleMatch ? titleMatch[1].trim() : `Recommendation ${index + 1}`;
        
        // Extract description - paragraph after title
        const descMatch = section.match(/\n(.+?)(?:\n\n|$)/);
        const description = descMatch ? descMatch[1].trim() : '';
        
        // Extract action items
        const actionItems = [];
        const actionRegex = /\d+\.\s+(.+?)(?:\n|$)/g;
        let actionMatch;
        while ((actionMatch = actionRegex.exec(section)) !== null) {
          actionItems.push(actionMatch[1].trim());
        }
        
        // Extract impact if available
        let impact = "Medium";
        if (section.toLowerCase().includes('impact: high')) impact = "High";
        if (section.toLowerCase().includes('impact: low')) impact = "Low";
        
        // Find related category
        const relatedCategory = this._findRelatedCategory(section, surveyData.categories);
        
        return {
          id: Date.now() + index,
          category: relatedCategory,
          title,
          description,
          impact,
          status: 'pending',
          confidence: this._getSecureRandomInt(75, 90),
          keyStats: [
            { label: "Potential Impact", value: this._getSecureRandomInt(60, 90), color: "green" }
          ],
          actionItems: actionItems.length > 0 ? actionItems : [
            "Action item 1",
            "Action item 2",
            "Action item 3",
            "Action item 4"
          ]
        };
      });
    } catch (error) {
      console.error('Error parsing recommendations:', error);
      return this._getFallbackRecommendations(surveyData);
    }
  }
  
  /**
   * Find the most relevant category based on recommendation text
   * @param {string} recommendationText - The text of the recommendation
   * @param {Array} categories - Available categories from survey
   * @returns {string} - Most relevant category name
   * @private
   */
  static _findRelatedCategory(recommendationText, categories) {
    if (!categories || !categories.length) return "General";
    
    const text = recommendationText.toLowerCase();
    
    // Find category that is mentioned in the text
    for (const category of categories) {
      if (text.includes(category.name.toLowerCase())) {
        return category.name;
      }
    }
    
    // Return the lowest scoring category as a fallback
    categories.sort((a, b) => a.score - b.score);
    return categories[0]?.name || "General";
  }
  
  /**
   * Parse the API response to extract action plan
   * @param {Object} response - The API response
   * @param {Array} selectedRecommendations - Recommendations that were selected
   * @returns {Object} - Structured action plan
   * @private
   */
  static _parseActionPlan(response, selectedRecommendations) {
    // In a real implementation, you would parse the API response properly
    try {
      const responseText = response.response || '';
      
      // Extract title
      const titleMatch = responseText.match(/(?:Title|Plan)[:\s]+([^\n]+)/i);
      const title = titleMatch ? titleMatch[1].trim() : "Action Plan";
      
      // Extract tasks
      const tasks = [];
      const taskRegex = /[-*\d]+\.\s+([^\n]+)/g;
      let taskMatch;
      let taskId = Date.now();
      
      while ((taskMatch = taskRegex.exec(responseText)) !== null) {
        tasks.push({
          id: taskId++,
          name: taskMatch[1].trim(),
          completed: false
        });
      }
      
      // If no tasks found, use recommendation action items
      if (tasks.length === 0) {
        selectedRecommendations.forEach(rec => {
          (rec.actionItems || []).forEach((item, idx) => {
            tasks.push({
              id: Date.now() + idx,
              name: item,
              completed: false
            });
          });
        });
      }
      
      return {
        id: Date.now(),
        title,
        source: selectedRecommendations.map(r => r.category).join(', ') + ' Analizi',
        status: 'planned',
        dueDate: this._getFutureDateString(14), // 2 weeks from now
        owner: 'İK Departmanı',
        progress: 0,
        tasks,
        expanded: false,
        fromAi: true
      };
    } catch (error) {
      console.error('Error parsing action plan:', error);
      return this._getFallbackActionPlan(selectedRecommendations);
    }
  }
  
  /**
   * Generate fallback recommendations if API fails
   * @param {Object} surveyData - Data from a completed survey
   * @returns {Array} - Fallback recommendations
   * @private
   */
  static _getFallbackRecommendations(surveyData) {
    // Sort categories by score (ascending)
    const sortedCategories = [...(surveyData.categories || [])].sort((a, b) => a.score - b.score);
    
    // Use the lowest scoring categories for recommendations
    return sortedCategories.slice(0, 3).map((category, index) => ({
      id: Date.now() + index,
      category: category.name,
      title: `Improve ${category.name}`,
      description: `The survey indicates that ${category.name.toLowerCase()} is an area that needs improvement with a score of ${category.score}/5.`,
      impact: category.score < 3 ? "High" : "Medium",
      status: 'pending',
      confidence: 75,
      keyStats: [
        { label: "Category Score", value: Math.round(category.score * 20), color: "green" }
      ],
      actionItems: [
        `Conduct a focused workshop on ${category.name.toLowerCase()}`,
        `Develop a ${category.name.toLowerCase()} improvement plan`,
        `Assign a dedicated team to address ${category.name.toLowerCase()} issues`,
        `Schedule bi-weekly check-ins on ${category.name.toLowerCase()} metrics`
      ]
    }));
  }
  
  /**
   * Generate a fallback action plan if API fails
   * @param {Array} selectedRecommendations - Recommendations selected by the user
   * @returns {Object} - Fallback action plan
   * @private
   */
  static _getFallbackActionPlan(selectedRecommendations) {
    const tasks = selectedRecommendations.flatMap((rec, recIndex) => 
      (rec.actionItems || []).map((item, itemIndex) => ({
        id: Date.now() + recIndex * 10 + itemIndex,
        name: item,
        completed: false
      }))
    );
    
    const categories = selectedRecommendations.map(r => r.category).filter(Boolean);
    const uniqueCategories = [...new Set(categories)];
    
    return {
      id: Date.now(),
      title: uniqueCategories.length > 0 
        ? `${uniqueCategories.join(' & ')} İyileştirme Planı` 
        : "İyileştirme Eylem Planı",
      source: uniqueCategories.join(', ') + ' Analizi',
      status: 'planned',
      dueDate: this._getFutureDateString(14), // 2 weeks from now
      owner: 'İK Departmanı',
      progress: 0,
      tasks,
      expanded: false,
      fromAi: true
    };
  }
  
  /**
   * Get a future date string in ISO format
   * @param {number} daysInFuture - Number of days in the future
   * @returns {string} - Date string in YYYY-MM-DD format
   * @private
   */
  static _getFutureDateString(daysInFuture) {
    const date = new Date();
    date.setDate(date.getDate() + daysInFuture);
    return date.toISOString().split('T')[0];
  }

  /**
   * Generate a secure random integer between min and max (inclusive)
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @returns {number} - Random integer
   * @private
   */
  static _getSecureRandomInt(min, max) {
    const range = max - min + 1;
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return min + (array[0] % range);
  }
}

export default AiService;